import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Home, MapPin, DollarSign, Clock, Plus, Eye, MessageSquare,
    CheckCircle, AlertCircle, Calendar, X, Upload
} from 'lucide-react';
import { ActionItems, ActivityFeed } from '@/components/ActivityFeed';

export default function AgentPortalPage() {
    const { user } = useAuth();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await API.get('/agent/my-listings');
            setListings(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const statusColor = (s) => ({
        pending: 'bg-amber-100 text-amber-700',
        active: 'bg-emerald-100 text-emerald-700',
        matched: 'bg-blue-100 text-blue-700',
        closed: 'bg-purple-100 text-purple-700',
    }[s] || 'bg-zinc-100 text-zinc-600');

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="agent-portal-page">
            <Navbar variant="light" />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-medium text-zinc-900 font-['Outfit']" data-testid="agent-portal-title">
                            Agent Portal
                        </h1>
                        <p className="text-sm text-zinc-500 mt-1">
                            Manage your enrolled listings. Properties activate at Day 91.
                        </p>
                    </div>
                    <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm" data-testid="agent-add-listing-btn">
                        <Plus className="w-4 h-4 mr-2" /> Enroll Listing
                    </Button>
                </div>

                {/* Smart Action Items */}
                <ActionItems />

                {/* Day 75 Approaching Alert */}
                {!loading && listings.filter(l => l.listing_status === 'pending' && (91 - (l.current_dom || 0)) <= 16).length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3" data-testid="day75-alert">
                        <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                        <div>
                            <div className="text-sm font-medium text-red-800">Day 91 Approaching</div>
                            <div className="text-xs text-red-600 mt-0.5">
                                {listings.filter(l => l.listing_status === 'pending' && (91 - (l.current_dom || 0)) <= 16).length} listing(s) will activate within 16 days. Ensure all details are accurate before they go live.
                            </div>
                        </div>
                    </div>
                )}

                {/* Day 91 Explainer + Bulk Import */}
                <div className="flex gap-4 mb-6">
                    <div className="flex-1 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                        <div>
                            <div className="text-sm font-medium text-amber-800">Day 91 Marketplace</div>
                            <div className="text-xs text-amber-700 mt-0.5">
                                Submit listings at Day 75+. They remain "Pending" until Day 91, then auto-activate on the marketplace.
                            </div>
                        </div>
                    </div>
                    <BulkImportCard onImport={load} />
                </div>

                {loading ? (
                    <div className="text-center py-12"><div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin mx-auto" /></div>
                ) : listings.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center shadow-sm">
                        <Home className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-zinc-900 mb-2 font-['Outfit']">No Enrolled Listings</h3>
                        <p className="text-sm text-zinc-500 mb-4">Enroll your first listing to get started with the Day 91 program.</p>
                        <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg">
                            <Plus className="w-4 h-4 mr-2" /> Enroll Listing
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {listings.map(l => {
                            const dom = l.current_dom || l.original_dom || 0;
                            const daysLeft = Math.max(0, 91 - dom);
                            return (
                                <div key={l.id} className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm" data-testid={`agent-listing-${l.id}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <div className="text-sm font-medium text-zinc-900">{l.property_address}</div>
                                            {(l.city || l.state) && (
                                                <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                                                    <MapPin className="w-3 h-3" />{[l.city, l.state].filter(Boolean).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge className={statusColor(l.listing_status)}>{l.listing_status}</Badge>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                                        <div className="bg-zinc-50 rounded-lg p-2.5">
                                            <span className="text-zinc-500">Asking</span>
                                            <div className="font-semibold text-zinc-900">${(l.asking_price || 0).toLocaleString()}</div>
                                        </div>
                                        <div className="bg-zinc-50 rounded-lg p-2.5">
                                            <span className="text-zinc-500">Days on Market</span>
                                            <div className="font-semibold text-zinc-900">{dom}</div>
                                        </div>
                                        <div className={`rounded-lg p-2.5 ${daysLeft > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                                            <span className={daysLeft > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                                                {daysLeft > 0 ? 'Days Until Active' : 'Status'}
                                            </span>
                                            <div className={`font-semibold ${daysLeft > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                                                {daysLeft > 0 ? daysLeft : 'Active'}
                                            </div>
                                        </div>
                                        <div className="bg-zinc-50 rounded-lg p-2.5">
                                            <span className="text-zinc-500">Views</span>
                                            <div className="font-semibold text-zinc-900 flex items-center gap-1">
                                                <Eye className="w-3 h-3" />{l.views || 0}
                                            </div>
                                        </div>
                                        <div className="bg-zinc-50 rounded-lg p-2.5">
                                            <span className="text-zinc-500">Inquiries</span>
                                            <div className="font-semibold text-zinc-900 flex items-center gap-1">
                                                <MessageSquare className="w-3 h-3" />{l.inquiries || 0}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {showCreate && <AgentListingModal onClose={() => { setShowCreate(false); load(); }} user={user} />}
        </div>
    );
}

function AgentListingModal({ onClose, user }) {
    const [form, setForm] = useState({
        property_address: '', city: '', state: '', zip_code: '',
        property_type: 'single_family', bedrooms: 3, bathrooms: 2,
        sqft: 0, year_built: 0, asking_price: 0, days_on_market: 75,
        description: '', financing_available: true, owner_finance_terms: '',
        agent_name: user?.name || '', agent_email: user?.email || '',
        agent_phone: '', agent_brokerage: '',
    });
    const [loading, setLoading] = useState(false);

    const submit = async () => {
        if (!form.property_address || !form.asking_price) return;
        setLoading(true);
        try {
            await API.post('/marketplace/my-listings', form);
            onClose();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to create');
        } finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="agent-listing-modal">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-zinc-900 font-['Outfit']">Enroll Listing</h2>
                    <button onClick={onClose}><X className="w-5 h-5 text-zinc-400" /></button>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-700">
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                    Submit listings at Day 75+. They auto-activate on the marketplace at Day 91.
                </div>

                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2"><Label className="text-xs">Property Address</Label>
                            <Input value={form.property_address} onChange={e => setForm(f => ({ ...f, property_address: e.target.value }))}
                                className="border-zinc-200 h-9 text-sm" data-testid="agent-listing-address" /></div>
                        <div><Label className="text-xs">City</Label>
                            <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                        <div><Label className="text-xs">State</Label>
                            <Input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                        <div><Label className="text-xs">Asking Price ($)</Label>
                            <Input type="number" value={form.asking_price || ''} onChange={e => setForm(f => ({ ...f, asking_price: parseFloat(e.target.value) || 0 }))}
                                className="border-zinc-200 h-9 text-sm" data-testid="agent-listing-price" /></div>
                        <div><Label className="text-xs font-semibold text-amber-700">Days on Market</Label>
                            <Input type="number" value={form.days_on_market} onChange={e => setForm(f => ({ ...f, days_on_market: parseInt(e.target.value) || 0 }))}
                                className="border-amber-300 h-9 text-sm" data-testid="agent-listing-dom" /></div>
                        <div><Label className="text-xs">Beds</Label>
                            <Input type="number" value={form.bedrooms} onChange={e => setForm(f => ({ ...f, bedrooms: parseInt(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                        <div><Label className="text-xs">Baths</Label>
                            <Input type="number" value={form.bathrooms} onChange={e => setForm(f => ({ ...f, bathrooms: parseFloat(e.target.value) || 0 }))} className="border-zinc-200 h-9 text-sm" /></div>
                    </div>
                    <div><Label className="text-xs">Description</Label>
                        <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="border-zinc-200 text-sm min-h-16" /></div>
                    <div><Label className="text-xs">Owner Finance Terms</Label>
                        <Textarea value={form.owner_finance_terms} onChange={e => setForm(f => ({ ...f, owner_finance_terms: e.target.value }))}
                            placeholder="e.g., 10% down, 8% interest, 12mo balloon" className="border-zinc-200 text-sm min-h-12" /></div>

                    <div className="border-t border-zinc-200 pt-4 mt-4">
                        <div className="text-xs font-semibold text-zinc-700 mb-2">Agent Information</div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Agent Name</Label>
                                <Input value={form.agent_name} onChange={e => setForm(f => ({ ...f, agent_name: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                            <div><Label className="text-xs">Agent Email</Label>
                                <Input value={form.agent_email} onChange={e => setForm(f => ({ ...f, agent_email: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                            <div><Label className="text-xs">Agent Phone</Label>
                                <Input value={form.agent_phone} onChange={e => setForm(f => ({ ...f, agent_phone: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                            <div><Label className="text-xs">Brokerage</Label>
                                <Input value={form.agent_brokerage} onChange={e => setForm(f => ({ ...f, agent_brokerage: e.target.value }))} className="border-zinc-200 h-9 text-sm" /></div>
                        </div>
                    </div>
                </div>
                <div className="flex gap-2 mt-6 pt-4 border-t border-zinc-200">
                    <Button onClick={submit} disabled={loading || !form.property_address || !form.asking_price}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="agent-submit-listing-btn">
                        {loading ? 'Enrolling...' : 'Enroll Listing'}
                    </Button>
                    <Button variant="outline" onClick={onClose} className="border-zinc-200">Cancel</Button>
                </div>
            </div>
        </div>
    );
}

function BulkImportCard({ onImport }) {
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true);
        setResult(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await API.post('/autonomy/bulk-import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setResult(data);
            if (data.imported > 0) onImport();
        } catch (err) { console.error(err); }
        finally { setImporting(false); }
    };

    return (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 w-72 shrink-0" data-testid="bulk-import-card">
            <div className="flex items-center gap-2 mb-2">
                <Upload className="w-4 h-4 text-blue-600" />
                <div className="text-sm font-medium text-blue-800">Bulk Import</div>
            </div>
            <div className="text-[11px] text-blue-600 mb-3">
                Upload CSV with columns: property_address, asking_price, days_on_market, city, state
            </div>
            <label className="block">
                <input type="file" accept=".csv" onChange={handleFile} className="hidden" data-testid="bulk-import-input" />
                <div className={`text-center py-2 px-3 rounded-lg border border-dashed cursor-pointer text-xs ${importing ? 'border-blue-300 bg-blue-100 text-blue-500' : 'border-blue-300 hover:bg-blue-100 text-blue-700'}`}>
                    {importing ? 'Importing...' : 'Choose CSV File'}
                </div>
            </label>
            {result && (
                <div className="mt-2 text-[11px]">
                    <span className="text-emerald-700 font-medium">{result.imported} imported</span>
                    {result.errors?.length > 0 && <span className="text-red-600 ml-2">{result.errors.length} errors</span>}
                </div>
            )}
        </div>
    );
}
